import React from 'react';
import { motion } from 'framer-motion';
import { Layout } from '../layout/Layout';
import { Check, Sparkles } from 'lucide-react';

const plans = [
  {
    name: 'Starter',
    price: '99',
    description: 'Perfect voor kleine bedrijven die willen starten met AI-klantenservice.',
    features: [
      'Tot 1.000 chatberichten per maand',
      'Basis AI-functionaliteit',
      'Nederlandse taalondersteuning',
      'E-mail support',
      'Standaard rapportages',
      'Basis integraties'
    ]
  },
  {
    name: 'Professional',
    price: '249',
    description: 'Ideaal voor groeiende bedrijven met uitgebreide service-behoeften.',
    features: [
      'Tot 5.000 chatberichten per maand',
      'Geavanceerde AI-functionaliteit',
      'Meertalige ondersteuning',
      'Priority support',
      'Uitgebreide rapportages',
      'Alle standaard integraties',
      'Custom branding',
      'API toegang'
    ],
    popular: true
  },
  {
    name: 'Enterprise',
    price: 'Op maat',
    description: 'Voor grote organisaties die een op maat gemaakte oplossing nodig hebben.',
    features: [
      'Onbeperkt aantal chatberichten',
      'Premium AI-functionaliteit',
      'Onbeperkte taalondersteuning',
      'Dedicated support team',
      'Custom rapportages',
      'Custom integraties',
      'SLA garantie',
      'On-premise optie',
      'Training en consultancy'
    ]
  }
];

export function PricingPage() {
  return (
    <Layout>
      <div className="bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          {/* Header */}
          <div className="text-center max-w-3xl mx-auto mb-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 bg-brand-50 text-brand-600 px-4 py-1.5 rounded-full font-medium mb-6"
            >
              <Sparkles size={20} />
              Transparante Prijzen
            </motion.div>
            <h1 className="text-4xl font-bold text-gray-900 mb-6">
              Kies het Plan dat bij U Past
            </h1>
            <p className="text-xl text-gray-600">
              Flexibele prijsplannen voor elke bedrijfsgrootte. Start klein en groei met ons mee.
            </p>
          </div>

          {/* Pricing Grid */}
          <div className="grid md:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow relative ${
                  plan.popular ? 'ring-2 ring-brand-500' : ''
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-brand-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                      Meest gekozen
                    </div>
                  </div>
                )}
                <div className="p-8">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {plan.name}
                  </h3>
                  <div className="flex items-baseline mb-4">
                    <span className="text-4xl font-bold text-gray-900">€{plan.price}</span>
                    {plan.price !== 'Op maat' && <span className="text-gray-500 ml-2">/maand</span>}
                  </div>
                  <p className="text-gray-600 mb-6">
                    {plan.description}
                  </p>
                  <button
                    className={`w-full py-3 rounded-lg font-semibold mb-8 ${
                      plan.popular
                        ? 'bg-brand-600 text-white hover:bg-brand-700'
                        : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                    } transition-colors`}
                  >
                    Start {plan.name} Plan
                  </button>
                  <ul className="space-y-4">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-brand-600 mt-0.5" />
                        <span className="text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>

          {/* FAQ Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-20 text-center"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Veel Gestelde Vragen
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Heeft u nog vragen? Neem contact met ons op voor een persoonlijk gesprek.
            </p>
            <button className="bg-gray-100 text-gray-900 px-8 py-3 rounded-lg font-semibold hover:bg-gray-200 transition-colors">
              Contact Opnemen
            </button>
          </motion.div>
        </div>
      </div>
    </Layout>
  );
}